import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useGeneralData } from "../../data/GeneralDataContext";
import '../../assets/css/mobile-menu.css';

const MobileResponsiveMenu = ({ 
    toggleMenu, 
    isMobile: propIsMobile, 
    menuVisible,
    isOpen: propIsOpen,
    setIsOpen: propSetIsOpen
}) => {
    // Always initialize hooks at the top level
    const [localIsOpen, setLocalIsOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const location = useLocation();
    const { isMobile: contextIsMobile } = useResponsive();
    const { evd_general_data } = useGeneralData();
    
    // Get general data for social links
    const generalData = evd_general_data && evd_general_data.length > 0 ? evd_general_data[0] : null;
    
    // Use prop value, then context value, then local state
    const isMobile = propIsMobile !== undefined ? propIsMobile : contextIsMobile;
    
    // Use parent state if provided, otherwise use local state
    const isOpen = propIsOpen !== undefined ? propIsOpen : localIsOpen;
    const handleSetIsOpen = (value) => {
        if (propSetIsOpen) {
            propSetIsOpen(value);
        } else {
            setLocalIsOpen(value);
        }
    };

    // Apply body transform only if we're managing our own state
    useEffect(() => {
        if (propSetIsOpen === undefined && isOpen) {
            document.body.style.transform = 'translateX(-280px)';
            document.body.style.transition = 'transform 0.3s ease-in-out';
        } else if (propSetIsOpen === undefined) {
            document.body.style.transform = 'translateX(0)';
        }
        
        return () => {
            if (propSetIsOpen === undefined) {
                document.body.style.transform = 'translateX(0)';
                document.body.style.transition = '';
            }
        };
    }, [isOpen, propSetIsOpen]);

    // Reset menu state when location changes
    useEffect(() => {
        if (propSetIsOpen === undefined) {
            setLocalIsOpen(false);
        }
    }, [location.pathname, propSetIsOpen]);

    const menuItems = [
        { text: 'HOME', to: '/' },
        { text: 'GALLERY', to: '/projects' },
        { text: 'ABOUT', to: '/about' },
        { text: 'CONTACT', to: '/contact' }
    ];

    // Check if a menu item is active based on the current location
    const isActive = (path) => {
        if (path === '/' && location.pathname === '/') {
            return true;
        }
        return location.pathname.startsWith(path) && path !== '/';
    };

    // Always render the component, but only show the button and menu when on mobile
    const shouldDisplay = isMobile;
    
    // Debug
    console.log('MobileResponsiveMenu - isMobile:', isMobile);
    console.log('MobileResponsiveMenu - contextIsMobile:', contextIsMobile);
    console.log('MobileResponsiveMenu - propIsMobile:', propIsMobile);
    console.log('MobileResponsiveMenu - windowWidth:', window.innerWidth);

    return (
        <>
            {shouldDisplay && (
                <button
                    onClick={() => handleSetIsOpen(true)}
                    className={`mobile-menu-button ${isOpen ? 'open' : ''}`}
                    aria-label="Open menu"
                >
                    ☰
                </button>
            )}

            <div className={`mobile-menu-container ${isOpen ? 'open' : ''}`} style={{ display: shouldDisplay ? 'block' : 'none' }}>
                <nav className="mobile-menu-nav">
                    <div className="mobile-menu-logo">
                        <img src="/images/favicon.png" alt="EVD Logo" />
                    </div>
                    
                    <ul className="mobile-menu-list">
                        {menuItems.map((item, index) => (
                            <li 
                                key={index} 
                                className={`mobile-menu-item ${isActive(item.to) ? 'active' : ''}`}
                            >
                                <Link
                                    to={item.to}
                                    className="mobile-menu-link"
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    onClick={() => handleSetIsOpen(false)}
                                    style={{ 
                                        color: isActive(item.to) ? '#a3a284' : '#000000'
                                    }}
                                >
                                    {item.text}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    
                    {generalData && (
                        <div className="mobile-menu-social">
                            {generalData.instagram_url && (
                                <a href={generalData.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                    <i className="bi bi-instagram"></i>
                                </a>
                            )}
                            {generalData.facebook_url && (
                                <a href={generalData.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                    <i className="bi bi-facebook"></i>
                                </a>
                            )}
                            {generalData.x_url && (
                                <a href={generalData.x_url} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                                    <i className="bi bi-twitter-x"></i>
                                </a>
                            )}
                        </div>
                    )}
                    
                    <div className="mobile-menu-footer">
                        © {new Date().getFullYear()} {generalData?.evd_copyright || 'EVD Construction'}
                    </div>
                </nav>
            </div>

            {isOpen && shouldDisplay && (
                <div
                    onClick={() => handleSetIsOpen(false)}
                    className="mobile-menu-overlay"
                />
            )}
        </>
    );
};

export default MobileResponsiveMenu;
